import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const LanguageContext = createContext(null);

// Translations
const translations = {
  fr: {
    // Header
    nav: {
      home: 'Accueil',
      about: 'À propos',
      projects: 'Projets',
      partners: 'Partenaires',
      contact: 'Contact'
    },
    // Hero
    hero: {
      badge: 'Développement & Ingénierie de Projets',
      title1: 'Développer le',
      title2: 'territoire',
      title3: 'Révéler la valeur',
      subtitle: 'One Green Dev imagine et structure des projets durables, là où le foncier rencontre le paysage.',
      cta_primary: 'Découvrir nos projets',
      cta_secondary: 'Nous contacter',
      stats: {
        projects: 'Projets',
        projects_sub: 'structurants',
        hectares: 'Hectares',
        hectares_sub: 'valorisés',
        years: 'Experts',
        years_sub: 'mobilisés'
      },
      scroll: 'Défiler'
    },
    // Track Record
    track: {
      label: 'Notre Impact',
      title: 'Des projets ancrés dans le territoire',
      stats: [
        { label: 'Hectares', sublabel: 'de foncier valorisé' },
        { label: 'Projets', sublabel: 'structurants' },
        { label: 'Experts', sublabel: 'mobilisés' },
        { label: 'Engagement', sublabel: 'projets durables' }
      ]
    },
    // About
    about: {
      philosophy: {
        label: 'Notre Philosophie',
        quote: 'Les projets les plus durables sont ceux qui savent',
        quote_highlights: ['prendre le temps', 'respecter le lieu', 'laisser parler le terrain'],
        description: "One Green Dev développe des projets ancrés dans le territoire, où le paysage devient structure et le foncier, une opportunité. Nous concevons des lieux qui s'inscrivent dans le temps, à la croisée de l'aménagement, du loisir et de l'investissement responsable."
      },
      who: {
        label: 'Qui sommes-nous',
        title: 'One Green Dev',
        subtitle: '(OGD)',
        description: "Une société de développement et d'ingénierie de projets spécialisée dans la valorisation foncière, l'aménagement du territoire et la réalisation de projets durables à forte valeur ajoutée."
      },
      domains: [
        { title: 'Valorisation Foncière', desc: 'Identification, structuration juridique et financière du foncier' },
        { title: 'Aménagement du Territoire', desc: 'Conception et réalisation de projets intégrés' },
        { title: 'Équipements & Loisirs', desc: 'Golfs, parcours sportifs, espaces paysagers' },
        { title: 'Immobilier de Destination', desc: 'Tourisme, hospitality, projets mixtes' }
      ],
      approach: {
        title: 'Notre Approche Intégrée',
        text1: "OGD intervient sur l'ensemble de la chaîne de valeur des projets fonciers, immobiliers et territoriaux : de l'identification du foncier et la structuration juridique et financière, jusqu'à la conception, la réalisation et la mise en œuvre opérationnelle des projets.",
        text2: "Nous accompagnons également la levée de financements, la création de sociétés de projets, et la mise en place de modèles économiques pérennes, au service de la valorisation des territoires et des investissements à long terme."
      },
      commitments: {
        title: 'Nos Engagements',
        items: [
          'Maîtrise complète de la chaîne de valeur',
          'Partenariats avec acteurs publics et communautés',
          'Modèles économiques pérennes',
          'Développement responsable et inclusif'
        ],
        quote: "Acteur engagé du développement responsable, OGD structure des partenariats fonciers et institutionnels pour créer des projets économiquement viables et socialement inclusifs."
      },
      team: {
        label: 'Notre Équipe',
        title: 'Expertise & Savoir-faire',
        description: "One Green Dev rassemble des experts en développement immobilier, ingénierie, finance et gestion de projets. Notre équipe pluridisciplinaire coordonne l'ensemble des intervenants techniques, financiers et institutionnels dans une logique de qualité, de maîtrise des coûts et de respect des délais.",
        stats: ['Experts', 'Pôles métiers', 'Engagement'],
        cta: "Échanger avec notre équipe"
      }
    },
    // Projects
    projects: {
      label: 'Nos Développements',
      title: 'Projets',
      title_accent: 'Structurants',
      description: 'Trois projets emblématiques qui façonnent le territoire de Songon et créent de la valeur durable.',
      cta: 'Découvrir le projet',
      cta_explore: 'Explorer cette opportunité',
      cta_internal: 'En savoir plus',
      bottom_text: "Vous avez un projet d'investissement ?",
      bottom_cta: 'Discuter avec notre équipe',
      items: {
        golf: {
          name: 'Le Golf de Songon',
          tagline: 'Équipement sportif & Loisirs',
          description: "Un parcours de golf 18 trous de standard international, intégré dans un écosystème paysager préservé. Un projet alliant sport, nature et développement responsable.",
          highlights: ['Parcours 18 trous', 'Clubhouse & Académie', 'Espaces paysagers', 'Resort intégré']
        },
        terres: {
          name: 'Les Terres de Songon',
          tagline: 'Immobilier de destination',
          description: "Un programme résidentiel conçu pour s'inscrire dans le temps : villas, duplex et appartements dans un cadre naturel structuré et sécurisé.",
          highlights: ['Villas & Duplex', 'Cadre paysager', 'Infrastructures', 'Services premium']
        },
        eastside: {
          name: 'Songon East-Side',
          tagline: 'Valorisation foncière',
          description: "Une opportunité d'investissement foncier sécurisée dans une zone à fort potentiel. Titre foncier consolidé, accompagnement dédié et perspective de valorisation long terme.",
          highlights: ['Titre foncier sécurisé', 'Zone stratégique', 'Valorisation garantie', 'Accompagnement complet'],
          badge: 'Offre Limitée'
        }
      }
    },
    // Partners
    partners: {
      label: 'Écosystème',
      title: 'Des partenariats de',
      title_accent: 'long terme',
      description: "One Green Dev privilégie des partenariats durables avec les acteurs publics, les communautés locales et les investisseurs, afin de créer des projets utiles, viables et pérennes.",
      categories: [
        { name: 'Finance & Investissement', desc: "Banques, fonds d'investissement" },
        { name: 'Construction & BTP', desc: 'Entreprises de construction' },
        { name: 'Architecture & Design', desc: "Cabinets d'architecture" },
        { name: 'Ingénierie & Technique', desc: "Bureaux d'études" },
        { name: 'Juridique & Notariat', desc: 'Conseils juridiques' },
        { name: 'Environnement', desc: 'Experts environnementaux' }
      ],
      cta_title: 'Rejoindre notre réseau de partenaires',
      cta_text: "Vous êtes un acteur institutionnel, une entreprise ou une communauté locale ? Discutons de la manière dont nous pouvons collaborer pour créer des projets à forte valeur ajoutée, économiquement viables et socialement inclusifs.",
      cta_button: 'Devenir partenaire'
    },
    // Footer
    footer: {
      cta_title: 'Une question sur nos projets ?',
      cta_subtitle: 'Notre équipe vous répond sous 24h',
      cta_button: 'Nous contacter',
      tagline: 'Développeur & Aménageur Territorial',
      nav_title: 'Navigation',
      projects_title: 'Nos Projets',
      legal_title: 'Légal',
      copyright: 'Tous droits réservés.'
    },
    // Contact Modal
    contact: {
      title: 'Nous contacter',
      subtitle: 'Nous vous répondrons sous 24h',
      name: 'Nom',
      email: 'Email',
      phone: 'Téléphone',
      subject: 'Sujet',
      subject_placeholder: 'Sélectionnez',
      message: 'Message',
      message_placeholder: 'Comment pouvons-nous vous aider ?',
      submit: 'Envoyer',
      sending: 'Envoi...',
      success: 'Votre message a été envoyé avec succès !',
      error: 'Une erreur est survenue.',
      subjects: {
        golf: 'Le Golf de Songon',
        terres: 'Les Terres de Songon',
        eastside: 'Songon East-Side',
        partnership: 'Partenariat',
        other: 'Autre'
      }
    },
    // Songon East-Side Page
    eastside: {
      hero: {
        badge: 'Opportunité Foncière',
        title: 'Songon East-Side',
        subtitle: 'Investissez dans le futur de la Côte d\'Ivoire',
        description: 'Un projet foncier stratégique situé dans la zone de développement prioritaire de Songon, offrant une opportunité unique de valorisation à long terme.',
        cta: 'Découvrir le master plan'
      },
      highlights: {
        title: 'Pourquoi investir ?',
        items: [
          { title: 'Titre Foncier Sécurisé', desc: 'Documentation juridique complète et sécurisée' },
          { title: 'Zone Stratégique', desc: 'Située dans le corridor de développement Abidjan-Songon' },
          { title: 'Fort Potentiel', desc: 'Valorisation estimée significative sur 5-10 ans' },
          { title: 'Accompagnement', desc: 'Suivi personnalisé de votre investissement' }
        ]
      },
      masterplan: {
        title: 'Master Plan Interactif',
        subtitle: 'Explorez le projet en détail'
      },
      details: {
        title: 'Caractéristiques du Projet',
        location: 'Localisation',
        location_value: 'Songon, District d\'Abidjan',
        surface: 'Surface totale',
        surface_value: '225+ hectares',
        status: 'Statut juridique',
        status_value: 'Titre foncier consolidé',
        access: 'Accessibilité',
        access_value: 'À 30 min du Plateau'
      },
      cta: {
        title: 'Intéressé par ce projet ?',
        text: 'Notre équipe est disponible pour répondre à toutes vos questions et vous accompagner dans votre démarche d\'investissement.',
        button: 'Prendre rendez-vous'
      }
    }
  },
  en: {
    // Header
    nav: {
      home: 'Home',
      about: 'About',
      projects: 'Projects',
      partners: 'Partners',
      contact: 'Contact'
    },
    // Hero
    hero: {
      badge: 'Development & Project Engineering',
      title1: 'Develop the',
      title2: 'territory',
      title3: 'Reveal its value',
      subtitle: 'One Green Dev designs and structures sustainable projects where land meets landscape.',
      cta_primary: 'Discover our projects',
      cta_secondary: 'Contact us',
      stats: {
        projects: 'Projects',
        projects_sub: 'landmark',
        hectares: 'Hectares',
        hectares_sub: 'developed',
        years: 'Experts',
        years_sub: 'mobilized'
      },
      scroll: 'Scroll'
    },
    // Track Record
    track: {
      label: 'Our Impact',
      title: 'Projects rooted in the territory',
      stats: [
        { label: 'Hectares', sublabel: 'of developed land' },
        { label: 'Projects', sublabel: 'landmark' },
        { label: 'Experts', sublabel: 'mobilized' },
        { label: 'Commitment', sublabel: 'sustainable projects' }
      ]
    },
    // About
    about: {
      philosophy: {
        label: 'Our Philosophy',
        quote: 'The most sustainable projects are those that know how to',
        quote_highlights: ['take time', 'respect the place', 'let the land speak'],
        description: "One Green Dev develops projects rooted in the territory, where landscape becomes structure and land becomes opportunity. We design places that stand the test of time, at the crossroads of development, leisure and responsible investment."
      },
      who: {
        label: 'Who we are',
        title: 'One Green Dev',
        subtitle: '(OGD)',
        description: "A development and project engineering company specializing in land valorization, territorial development and the realization of sustainable high value-added projects."
      },
      domains: [
        { title: 'Land Valorization', desc: 'Identification, legal and financial structuring' },
        { title: 'Territorial Development', desc: 'Design and realization of integrated projects' },
        { title: 'Sports & Leisure', desc: 'Golf courses, sports facilities, landscape areas' },
        { title: 'Destination Real Estate', desc: 'Tourism, hospitality, mixed-use projects' }
      ],
      approach: {
        title: 'Our Integrated Approach',
        text1: "OGD operates across the entire value chain of land, real estate and territorial projects: from land identification and legal/financial structuring, to design, implementation and operational execution.",
        text2: "We also support fundraising, project company creation, and the implementation of sustainable economic models, serving territorial valorization and long-term investments."
      },
      commitments: {
        title: 'Our Commitments',
        items: [
          'Complete value chain mastery',
          'Partnerships with public actors and communities',
          'Sustainable economic models',
          'Responsible and inclusive development'
        ],
        quote: "A committed actor in responsible development, OGD structures land and institutional partnerships to create economically viable and socially inclusive projects."
      },
      team: {
        label: 'Our Team',
        title: 'Expertise & Know-how',
        description: "One Green Dev brings together experts in real estate development, engineering, finance and project management. Our multidisciplinary team coordinates all technical, financial and institutional stakeholders with a focus on quality, cost control and deadline compliance.",
        stats: ['Experts', 'Business units', 'Commitment'],
        cta: "Talk to our team"
      }
    },
    // Projects
    projects: {
      label: 'Our Developments',
      title: 'Landmark',
      title_accent: 'Projects',
      description: 'Three flagship projects shaping the Songon territory and creating sustainable value.',
      cta: 'Discover the project',
      cta_explore: 'Explore this opportunity',
      cta_internal: 'Learn more',
      bottom_text: 'Do you have an investment project?',
      bottom_cta: 'Talk to our team',
      items: {
        golf: {
          name: 'Le Golf de Songon',
          tagline: 'Sports & Leisure Facility',
          description: 'An 18-hole golf course of international standard, integrated into a preserved landscape ecosystem. A project combining sport, nature and responsible development.',
          highlights: ['18-hole course', 'Clubhouse & Academy', 'Landscape areas', 'Integrated resort']
        },
        terres: {
          name: 'Les Terres de Songon',
          tagline: 'Destination Real Estate',
          description: 'A residential program designed to stand the test of time: villas, duplexes and apartments in a structured and secure natural setting.',
          highlights: ['Villas & Duplexes', 'Landscaped setting', 'Infrastructure', 'Premium services']
        },
        eastside: {
          name: 'Songon East-Side',
          tagline: 'Land Valorization',
          description: 'A secure land investment opportunity in a high-potential area. Consolidated land title, dedicated support and long-term appreciation potential.',
          highlights: ['Secured land title', 'Strategic zone', 'Guaranteed appreciation', 'Full support'],
          badge: 'Limited Offer'
        }
      }
    },
    // Partners
    partners: {
      label: 'Ecosystem',
      title: 'Long-term',
      title_accent: 'partnerships',
      description: 'One Green Dev favors sustainable partnerships with public actors, local communities and investors, to create useful, viable and lasting projects.',
      categories: [
        { name: 'Finance & Investment', desc: 'Banks, investment funds' },
        { name: 'Construction & Civil Works', desc: 'Construction companies' },
        { name: 'Architecture & Design', desc: 'Architecture firms' },
        { name: 'Engineering & Technical', desc: 'Engineering offices' },
        { name: 'Legal & Notary', desc: 'Legal counsel' },
        { name: 'Environment', desc: 'Environmental experts' }
      ],
      cta_title: 'Join our partner network',
      cta_text: 'Are you an institutional player, a company or a local community? Let\'s discuss how we can collaborate to create high value-added projects that are economically viable and socially inclusive.',
      cta_button: 'Become a partner'
    },
    // Footer
    footer: {
      cta_title: 'Questions about our projects?',
      cta_subtitle: 'Our team responds within 24h',
      cta_button: 'Contact us',
      tagline: 'Territorial Developer & Planner',
      nav_title: 'Navigation',
      projects_title: 'Our Projects',
      legal_title: 'Legal',
      copyright: 'All rights reserved.'
    },
    // Contact Modal
    contact: {
      title: 'Contact us',
      subtitle: 'We will respond within 24h',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      subject: 'Subject',
      subject_placeholder: 'Select',
      message: 'Message',
      message_placeholder: 'How can we help you?',
      submit: 'Send',
      sending: 'Sending...',
      success: 'Your message has been sent successfully!',
      error: 'An error occurred.',
      subjects: {
        golf: 'Le Golf de Songon',
        terres: 'Les Terres de Songon',
        eastside: 'Songon East-Side',
        partnership: 'Partnership',
        other: 'Other'
      }
    },
    // Songon East-Side Page
    eastside: {
      hero: {
        badge: 'Land Opportunity',
        title: 'Songon East-Side',
        subtitle: 'Invest in the future of Ivory Coast',
        description: 'A strategic land project located in the priority development zone of Songon, offering a unique long-term valorization opportunity.',
        cta: 'Discover the master plan'
      },
      highlights: {
        title: 'Why invest?',
        items: [
          { title: 'Secured Land Title', desc: 'Complete and secured legal documentation' },
          { title: 'Strategic Zone', desc: 'Located in the Abidjan-Songon development corridor' },
          { title: 'High Potential', desc: 'Significant estimated appreciation over 5-10 years' },
          { title: 'Support', desc: 'Personalized monitoring of your investment' }
        ]
      },
      masterplan: {
        title: 'Interactive Master Plan',
        subtitle: 'Explore the project in detail'
      },
      details: {
        title: 'Project Features',
        location: 'Location',
        location_value: 'Songon, Abidjan District',
        surface: 'Total surface',
        surface_value: '225+ hectares',
        status: 'Legal status',
        status_value: 'Consolidated land title',
        access: 'Accessibility',
        access_value: '30 min from Plateau'
      },
      cta: {
        title: 'Interested in this project?',
        text: 'Our team is available to answer all your questions and support you in your investment process.',
        button: 'Schedule a meeting'
      }
    }
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('ogd_lang') || 'fr';
    }
    return 'fr';
  });

  useEffect(() => {
    localStorage.setItem('ogd_lang', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = useCallback((key) => {
    const keys = key.split('.');
    let result = translations[language];
    for (const k of keys) {
      result = result?.[k];
      if (result === undefined) {
        // Fallback to French
        result = translations.fr;
        for (const fk of keys) {
          result = result?.[fk];
          if (result === undefined) return key;
        }
        return result;
      }
    }
    return result;
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'fr' ? 'en' : 'fr');
  };

  const value = useMemo(() => ({ language, setLanguage, toggleLanguage, t }), [language, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
